import { Link } from 'react-router-dom'
import {
  AddressBlock,
  Confidence,
  RecordGrid,
  Segmented,
  TypeBadge,
  typeOf,
} from '../Page'
import { Chevron } from '../Page'
import { Glyph } from './Glyph'
import { CitePanel, PermalinkPanel } from './Popovers'
import { PassageText, DoubtNote } from './Text'
import { factsFromCitation } from './citation'
import {
  addressRows,
  columnsLabel,
  iiifPath,
  measureLabel,
  passagesOf,
  permalinkPath,
  regionName,
  toPt,
  type PageGeometry,
  type RegionItem,
} from './model'
import type { RegionRecord } from './useRegionDetail'
import type { PageDetail } from '../../lib/api'

/**
 * The inspector: the physical address of the thing the reader chose.
 *
 * Everything here is provenance. The two coordinate spaces, the column span,
 * the engine that read it, the confidence figure and the region's own IIIF
 * path are the product's claim made visible, so they come before the reading
 * text rather than after it.
 */

export type InspectorPopover = 'permalink' | 'cite' | 'more' | null

export function Inspector({
  page,
  geometry,
  region,
  record,
  query,
  corrected,
  onCorrected,
  onClose,
  onClip,
  canClip,
  productName,
  dense = false,
  regionCount,
  popover,
  onPopover,
}: {
  page: PageDetail
  geometry: PageGeometry
  region: RegionItem | null
  record: RegionRecord | null
  query: string
  corrected: boolean
  onCorrected: (next: boolean) => void
  onClose: () => void
  onClip: () => void
  canClip: boolean
  productName: string
  dense?: boolean
  regionCount: number
  popover: InspectorPopover
  onPopover: (next: InspectorPopover) => void
}) {
  const setPopover = onPopover

  if (!region || !record) {
    return (
      <div className="inspector flex w-full flex-col justify-start p-4">
        <div className="chrome mb-2">Nothing selected</div>
        <p className="text-[15px] leading-normal text-ink2">
          Choose a region on the page. Every region has a physical address: where it sits in raster
          pixels and in printed points, which columns it spans, and how its text was obtained.
        </p>
      </div>
    )
  }

  const type = typeOf(region.rawType)
  const passages = passagesOf(region, corrected)
  const blockCount = region.blocks.length
  const engine = region.blocks[0]?.source_engine ?? geometry.engine ?? '-'
  const fontMeta = region.blocks.some((block) => block.font_meta) ? 'present' : 'null'
  const permalink = permalinkPath(geometry, region.bbox)
  const iiif = iiifPath(page.iiif_id, region.bbox)
  const columns = record.columnsLabel ?? columnsLabel(region.bbox, geometry)
  const corrections = region.blocks.some((block) => block.corrected_text)
  const origin = record.isWire
    ? `wire · ${record.wireService ?? 'unattributed'}`
    : `local · ${record.byline ? 'staff' : 'unattributed'}`
  const readingHref = record.unitId
    ? `/unit/${record.unitId}`
    : record.articleId
      ? `/article/${record.articleId}`
      : null

  const facts = factsFromCitation(record.citation, {
    headline: record.title,
    author: record.byline,
    publication: geometry.publication,
    date: geometry.pubDate,
    pageLabel: geometry.label,
    columns,
    permalink: `${window.location.origin}${permalink}`,
    wireService: record.wireService,
    productName,
  })

  return (
    <div className="inspector relative flex min-h-0 w-full flex-col">
      {/* ── who and what ─────────────────────────────────────────────── */}
      <div className="flex-none border-b border-n300 px-4 pb-3 pt-3">
        <div className="mb-2 flex items-center gap-2">
          <TypeBadge type={region.rawType} />
          <div className="chrome">{type.name}</div>
          <div className="font-mono text-[10px] text-ink3">
            {dense
              ? `ORD ${String(region.ordinal).padStart(2, '0')}${
                  record.confidence !== null && record.confidence !== undefined
                    ? ` · ${(record.confidence * 100).toFixed(1)}%`
                    : ''
                }`
              : `ORD ${String(region.ordinal).padStart(2, '0')} · R${String(region.ordinal).padStart(2, '0')}`}
          </div>
          {record.needsReview && (
            <div className="ml-auto font-mono text-[10px] tracking-[0.06em] text-mark">
              REVIEW QUEUED
            </div>
          )}
          <button
            type="button"
            aria-label="Clear the selection"
            onClick={onClose}
            className={`${record.needsReview ? '' : 'ml-auto'} flex h-6 w-6 items-center justify-center text-ink2`}
          >
            <Glyph name="close" size={12} />
          </button>
        </div>
        {/* A title the recognition ran together can be hundreds of words long.
            Clamping it keeps the physical address on screen, which is what the
            panel exists for; the whole string is still one hover away and the
            reading text below is unclipped. */}
        <h2
          className={`mb-1.5 overflow-hidden font-semibold leading-[1.1] ${
            dense ? 'text-[21px]' : 'text-[25px]'
          }`}
          title={record.title ?? undefined}
          style={{
            letterSpacing: '-0.012em',
            textWrap: 'balance',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {regionName(region.rawType, record.title)}
        </h2>
        {record.subtitle && !dense && (
          <div
            className="mb-2 overflow-hidden text-[16px] italic leading-[1.35] text-ink2"
            style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}
          >
            {record.subtitle}
          </div>
        )}
        <div className="flex items-baseline gap-2 text-[13.5px] text-ink2">
          {record.byline ? (
            <span>
              {/* A photograph's credit and an advertisement's advertiser are
                  not bylines, so they are not prefixed as if they were. */}
              {region.typeKey === 'article' || region.typeKey === 'notice' ? 'By ' : ''}
              <Link to={`/search?author=${encodeURIComponent(cleanByline(record.byline))}`}>
                {cleanByline(record.byline)}
              </Link>
            </span>
          ) : (
            <span>Unattributed</span>
          )}
          <span className="text-rule2">·</span>
          <span className="truncate">
            {record.wireService ?? geometry.publication ?? 'This publication'}
          </span>
          {!dense && (
            <span className="ml-auto whitespace-nowrap font-mono text-[10.5px] uppercase text-ink2">
              {origin}
            </span>
          )}
        </div>
      </div>

      {/* ── the physical address ─────────────────────────────────────── */}
      {dense ? (
        <div className="flex-none border-b border-n300 bg-surface px-3.5 py-2.5 font-mono text-[11.5px] leading-[1.7]">
          <div>
            {`${Math.round(region.bbox[0])}, ${Math.round(region.bbox[1])} · ${Math.round(
              region.bbox[2] - region.bbox[0],
            )} × ${Math.round(region.bbox[3] - region.bbox[1])} px`}
          </div>
          <div className="text-ink2">
            {`${toPt(region.bbox[0], geometry.ppi).toFixed(1)}, ${toPt(
              region.bbox[1],
              geometry.ppi,
            ).toFixed(1)} pt · cols ${columns} · ${measureLabel(region.bbox, geometry)}`}
          </div>
        </div>
      ) : (
        <>
          <div className="flex-none border-b border-n300">
            <AddressBlock
              rows={addressRows(region.bbox, geometry)}
              iiif={iiif}
              onCopy={() =>
                void navigator.clipboard?.writeText(
                  addressRows(region.bbox, geometry)
                    .map(([label, value]) => `${label} ${value}`)
                    .join('\n'),
                )
              }
            />
          </div>

          <div className="flex-none border-b border-n300 px-4 py-3">
            <RecordGrid
              rows={[
                [
                  'page',
                  page.section_label
                    ? `${page.section_label} · section ${page.section_label.replace(/[^A-Za-z].*$/, '')}`
                    : `p. ${page.page_number}`,
                ],
                ['order', `${String(region.ordinal).padStart(2, '0')} of ${regionCount}`],
                ['words', String(record.wordCount)],
                ['blocks', String(blockCount)],
                ['engine', engine],
                ['font_meta', fontMeta],
                ['origin', origin],
                [
                  'confidence',
                  <span
                    key="c"
                    className={
                      record.confidence !== null &&
                      record.confidence !== undefined &&
                      record.confidence < 0.85
                        ? 'text-mark'
                        : 'text-art'
                    }
                  >
                    {record.confidence !== null && record.confidence !== undefined
                      ? `${(record.confidence * 100).toFixed(1)}%`
                      : 'not recorded'}
                  </span>,
                ],
                ['article', record.articleId ? shortId(record.articleId) : '-'],
                ['jump', record.jump?.to ? `${record.jump.to}` : '-'],
              ]}
            />
          </div>
        </>
      )}

      {/* ── what a reader does with it ───────────────────────────────── */}
      {!dense && (
        <div className="relative flex flex-none items-center gap-2 border-b border-n300 px-4 py-3">
          <button
            type="button"
            className="btn-primary px-3.5 py-1.5"
            onClick={onClip}
            disabled={!canClip}
            title={canClip ? 'Save this region as a clipping' : 'Sign in to save clippings'}
          >
            Save clipping
          </button>
          <button
            type="button"
            className="btn-secondary px-3.5 py-1.5"
            onClick={() => setPopover(popover === 'cite' ? null : 'cite')}
          >
            Cite
          </button>
          <button
            type="button"
            className="btn-secondary px-3.5 py-1.5"
            onClick={() => setPopover(popover === 'permalink' ? null : 'permalink')}
          >
            Permalink
          </button>
          <button
            type="button"
            aria-label="More actions for this region"
            className="btn-secondary ml-auto px-2.5 py-1.5 text-ink2"
            onClick={() => setPopover(popover === 'more' ? null : 'more')}
          >
            ···
          </button>

          {popover === 'permalink' && (
            <PermalinkPanel permalink={permalink} iiif={iiif} onClose={() => setPopover(null)} />
          )}
          {popover === 'cite' && (
            <CitePanel facts={facts} provided={record.citation} onClose={() => setPopover(null)} />
          )}
          {popover === 'more' && (
            <div className="popover absolute left-4 right-4 top-full z-40 mt-1.5 flex flex-col p-1.5">
              <button
                type="button"
                className="btn-ghost justify-start px-2 py-1.5 text-[13.5px]"
                onClick={() => {
                  if (iiif) void navigator.clipboard?.writeText(iiif)
                  setPopover(null)
                }}
              >
                Copy the IIIF path
              </button>
              <Link
                to={`/compare?left=${page.id}`}
                className="btn-ghost justify-start px-2 py-1.5 text-[13.5px] no-underline"
              >
                Compare this page with another
              </Link>
              {readingHref && (
                <Link
                  to={readingHref}
                  className="btn-ghost justify-start px-2 py-1.5 text-[13.5px] no-underline"
                >
                  Open the reading view
                </Link>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── what the paper printed ───────────────────────────────────── */}
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-none items-center gap-2.5 px-4 pb-2.5 pt-3">
          <Segmented
            ariaLabel="Which text to show"
            value={corrected ? 'corrected' : 'recognised'}
            onChange={(next) => onCorrected(next === 'corrected')}
            options={[
              { value: 'recognised', label: 'Recognised' },
              {
                value: 'corrected',
                label: 'Corrected',
                disabled: !corrections,
                title: corrections
                  ? 'A labelled reading aid. The raw text stays the record.'
                  : 'Nothing has been corrected in this region',
              },
            ]}
            size="sm"
          />
          {!corrections && (
            <div className="font-mono text-[10px] leading-[1.35] text-ink3">
              NO CORRECTIONS
              <br />
              RECORDED
            </div>
          )}
          {corrections && record.correctionSource && (
            <div className="font-mono text-[10px] leading-[1.35] text-ink3">
              CORRECTED BY
              <br />
              {record.correctionSource.toUpperCase()}
            </div>
          )}
          <div className="ml-auto">
            <Confidence value={record.confidence} />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3" style={{ overscrollBehavior: 'contain' }}>
          {passages.length ? (
            <>
              <PassageText passages={passages} query={query} size={dense ? 15.5 : 16} />
              <DoubtNote passages={passages} reviewQueued={record.needsReview} />
              {corrected && (
                <p className="ident mt-2 text-mark">
                  Corrected text is a labelled reading aid. The recognised text is the record of
                  what the paper printed and is never overwritten.
                </p>
              )}
            </>
          ) : (
            <p className="text-[15px] leading-normal text-ink2">
              {type.name === 'Photograph'
                ? 'A photograph carries no text layer. Its caption and credit are the record, and the crop above is the evidence.'
                : 'No text was extracted for this region.'}
            </p>
          )}
        </div>

        {dense ? (
          <div className="flex flex-none gap-1.5 border-t border-n300 px-3.5 py-2.5">
            <button
              type="button"
              className="btn-primary h-11 flex-1 text-[14.5px]"
              onClick={onClip}
              disabled={!canClip}
            >
              Clip
            </button>
            <button
              type="button"
              className="btn-secondary h-11 flex-1 text-[14.5px]"
              onClick={() => setPopover(popover === 'cite' ? null : 'cite')}
            >
              Cite
            </button>
            <button
              type="button"
              aria-label="Copy the permalink"
              className="btn-secondary h-11 w-11 px-0"
              onClick={() => setPopover(popover === 'permalink' ? null : 'permalink')}
            >
              <Glyph name="jump" size={15} />
            </button>
            {popover === 'permalink' && (
              <PermalinkPanel permalink={permalink} iiif={iiif} onClose={() => setPopover(null)} />
            )}
            {popover === 'cite' && (
              <CitePanel facts={facts} provided={record.citation} onClose={() => setPopover(null)} />
            )}
          </div>
        ) : (
          <div className="flex flex-none items-center justify-between border-t border-n300 px-4 py-2.5 font-mono text-[10px] text-ink2">
            <div>
              {`${record.wordCount} WORDS · ${blockCount} BLOCK${blockCount === 1 ? '' : 'S'}`}
              {record.jump?.to ? ` · JUMP TO ${record.jump.to}` : ''}
            </div>
            {readingHref ? (
              <Link
                to={readingHref}
                className="flex items-center gap-1 text-[10px] tracking-[0.06em] no-underline"
              >
                FULL TEXT
                <Chevron size={10} />
              </Link>
            ) : (
              <span className="text-ink3">NO FULL TEXT RECORD</span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id
}

function cleanByline(byline: string): string {
  return byline.replace(/^by\s+/i, '').trim()
}
